import { useState } from 'react'
import { Mic, MicOff, Volume2, VolumeX } from 'lucide-react'
import Modal from './Modal'
import SearchSpacesModal from './SearchSpacesModal'
import SpaceSettingsModal from './SpaceSettingsModal'
import ProfileDock from './ProfileDock'
import { useVoice } from '../contexts/VoiceContext'
import { soundManagerFiles } from '../../lib/sound-manager-files'

/**
 * Panel 1: Spaces/DMs Sidebar
 * Thin left sidebar (w-16 desktop) with circular space icons
 * Bottom section has user profile widget with voice controls
 */
export default function SpacesSidebar({
  spaces,
  selectedSpace,
  onSelectSpace,
  viewMode,
  onChangeViewMode,
  profile,
  onOpenSettings,
  onOpenProfile,
  onOpenVoiceSettings,
  onSignOut,
  onCreateSpace,
  onJoinSpace,
  onUpdateSpace,
  onDeleteSpace,
  mobileMenuOpen,
  onToggleMobileMenu,
  toast,
  // Notification props
  notifications = [],
  unreadCount = 0,
  onMarkAsRead,
  onMarkAllAsRead,
  onUpdateStatus,
  onSwitchStyle,
}) {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showSearchSpaces, setShowSearchSpaces] = useState(false)
  const [showSpaceSettings, setShowSpaceSettings] = useState(false)
  const [selectedSpaceForSettings, setSelectedSpaceForSettings] = useState(null)
  const [newSpaceName, setNewSpaceName] = useState('')
  const [hoveredSpaceId, setHoveredSpaceId] = useState(null)

  // Use global voice state from context
  const { isMuted, isDeafened, toggleMute, toggleDeafen } = useVoice()

  const handleCreateSpace = async (e) => {
    e.preventDefault()
    if (!newSpaceName.trim()) return

    await onCreateSpace(newSpaceName)
    setNewSpaceName('')
    setShowCreateModal(false)
  }


  return (
    <>
      {/* Mobile Menu Toggle */}
      <button
        onClick={onToggleMobileMenu}
        className="md:hidden fixed top-4 left-4 z-50 p-2 glass-strong rounded-lg"
      >
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Sidebar */}
      <div
        className={`
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0
          fixed md:relative
          z-40
          h-full w-16
          glass-strong border-r border-gjyl-slate-700/50
          flex flex-col items-center
          py-4
          transition-transform duration-300
        `}
      >
        {/* GJYL Logo - Click to switch to Jarvis mode */}
        <button
          onClick={() => onSwitchStyle?.('jarvis')}
          className="mb-3 group relative"
          title="Switch to Jarvis Mode"
        >
          <img
            src="/logos/gjyl-logo.png"
            alt="GJYL"
            className="w-10 h-10 rounded-xl object-contain transition-all duration-300 group-hover:scale-110 group-hover:shadow-[0_0_20px_rgba(99,102,241,0.6)]"
          />
          <div className="absolute inset-0 rounded-xl bg-gjyl-indigo-500/0 group-hover:bg-gjyl-indigo-500/10 transition-colors" />
        </button>

        {/* Divider */}
        <div className="w-8 h-px bg-gjyl-slate-700 mb-2"></div>

        {/* DMs Button */}
        <button
          onClick={() => onChangeViewMode('dms')}
          className={`
            w-12 h-12 rounded-2xl mb-2
            flex items-center justify-center
            transition-all duration-200
            ${viewMode === 'dms'
              ? 'bg-gjyl-indigo-500 text-white shadow-gjyl-glow'
              : 'glass hover:glass-strong text-slate-400 hover:text-white'
            }
          `}
          title="Direct Messages"
          aria-label="Direct Messages"
          aria-pressed={viewMode === 'dms'}
          data-testid="dms-button"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"
            />
          </svg>
        </button>

        {/* Divider */}
        <div className="w-8 h-px bg-gjyl-slate-700 mb-2"></div>

        {/* Spaces List */}
        <div className="flex-1 overflow-y-auto scrollbar-gjyl w-full flex flex-col items-center space-y-2 px-2">
          {spaces.map((space) => (
            <div
              key={space.id}
              className="relative group"
              onMouseEnter={() => setHoveredSpaceId(space.id)}
              onMouseLeave={() => setHoveredSpaceId(null)}
            >
              <button
                onClick={() => {
                  onSelectSpace(space)
                  onChangeViewMode('spaces')
                }}
                className={`
                  w-12 h-12 rounded-2xl
                  flex items-center justify-center
                  font-semibold text-sm
                  transition-all duration-200
                  hover:rounded-xl
                  ${selectedSpace?.id === space.id && viewMode === 'spaces'
                    ? 'bg-gjyl-indigo-500 text-white shadow-gjyl-glow rounded-xl'
                    : 'bg-gjyl-slate-700 text-slate-300 hover:bg-gjyl-indigo-500/50 hover:text-white'
                  }
                `}
                title={space.name}
                aria-label={`Go to ${space.name} space`}
                aria-pressed={selectedSpace?.id === space.id && viewMode === 'spaces'}
                data-testid={`space-button-${space.id}`}
              >
                {space.icon_url ? (
                  <img src={space.icon_url} alt={space.name} className="w-full h-full rounded-2xl object-cover" />
                ) : (
                  space.name.substring(0, 2).toUpperCase()
                )}
              </button>

              {/* Settings button (only for space owners) */}
              {space.owner_id === profile.id && hoveredSpaceId === space.id && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedSpaceForSettings(space)
                    setShowSpaceSettings(true)
                  }}
                  className="absolute -top-1.5 -right-1.5 w-7 h-7 bg-gjyl-slate-800 hover:bg-gjyl-indigo-500 border border-gjyl-slate-600 rounded-full flex items-center justify-center transition-all hover:scale-110 shadow-gjyl-lift z-10"
                  title="Space Settings"
                >
                  <svg className="w-4 h-4 text-slate-400 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>
              )}
            </div>
          ))}

          {/* Add Space Button */}
          <button
            onClick={() => setShowCreateModal(true)}
            className="w-12 h-12 rounded-2xl glass hover:glass-strong text-green-400 hover:text-green-300 transition-all duration-200 flex items-center justify-center group"
            title="Create Space"
            aria-label="Create a new space"
            data-testid="create-space-button"
          >
            <svg className="w-6 h-6 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>

          {/* Discover/Search Spaces Button */}
          <button
            onClick={() => setShowSearchSpaces(true)}
            className="w-12 h-12 rounded-2xl glass hover:glass-strong text-purple-400 hover:text-purple-300 transition-all duration-200 flex items-center justify-center group"
            title="Discover Spaces"
            aria-label="Discover and join public spaces"
            data-testid="discover-spaces-button"
          >
            <svg className="w-6 h-6 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>
        </div>

        {/* Bottom: User Profile Widget with Voice Controls */}
        <div className="mt-auto pt-4 border-t border-gjyl-slate-700/50 w-full flex flex-col items-center space-y-2">
          {/* Microphone Mute */}
          <button
            onClick={() => {
              toggleMute()
              // Play appropriate sound
              if (isMuted) {
                soundManagerFiles.playUnmute()
              } else {
                soundManagerFiles.playMute()
              }
            }}
            className={`w-11 h-11 rounded-gjyl transition-all duration-200 flex items-center justify-center ${
              isMuted
                ? 'bg-gjyl-danger/20 text-gjyl-danger border border-gjyl-danger/50 hover:bg-gjyl-danger/30'
                : 'glass hover:glass-strong text-green-500 hover:text-green-400'
            }`}
            title={isMuted ? 'Unmute' : 'Mute'}
            aria-label={isMuted ? 'Unmute microphone' : 'Mute microphone'}
            aria-pressed={isMuted}
            data-testid="mute-button"
          >
            {isMuted ? (
              <MicOff className="w-5 h-5" aria-hidden="true" />
            ) : (
              <Mic className="w-5 h-5" aria-hidden="true" />
            )}
          </button>

          {/* Headphones/Deafen */}
          <button
            onClick={() => {
              toggleDeafen()
              // Play appropriate sound
              if (isDeafened) {
                soundManagerFiles.playUndeafen()
              } else {
                soundManagerFiles.playDeafen()
              }
            }}
            className={`w-11 h-11 rounded-gjyl transition-all duration-200 flex items-center justify-center ${
              isDeafened
                ? 'bg-gjyl-danger/20 text-gjyl-danger border border-gjyl-danger/50 hover:bg-gjyl-danger/30'
                : 'glass hover:glass-strong text-green-500 hover:text-green-400'
            }`}
            title={isDeafened ? 'Undeafen' : 'Deafen'}
            aria-label={isDeafened ? 'Undeafen audio' : 'Deafen audio'}
            aria-pressed={isDeafened}
            data-testid="deafen-button"
          >
            {isDeafened ? (
              <VolumeX className="w-5 h-5" aria-hidden="true" />
            ) : (
              <Volume2 className="w-5 h-5" aria-hidden="true" />
            )}
          </button>

          {/* User Settings - Opens full settings modal */}
          <button
            onClick={onOpenSettings}
            className="w-11 h-11 rounded-gjyl glass hover:glass-strong text-slate-400 hover:text-white transition-all duration-200 flex items-center justify-center hover:rotate-45"
            title="Settings"
            aria-label="Settings"
            data-testid="settings-button"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>

          {/* User Profile Dock with Notification Badge */}
          <ProfileDock
            profile={profile}
            notifications={notifications}
            unreadCount={unreadCount}
            onMarkAsRead={onMarkAsRead}
            onMarkAllAsRead={onMarkAllAsRead}
            onOpenFullProfile={onOpenProfile}
            onOpenSettings={onOpenSettings}
            onUpdateStatus={onUpdateStatus}
          />
        </div>
      </div>

      {/* Create Space Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create a Space"
        size="small"
      >
        <form onSubmit={handleCreateSpace} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Space Name
            </label>
            <input
              type="text"
              value={newSpaceName}
              onChange={(e) => setNewSpaceName(e.target.value)}
              className="input-gjyl"
              placeholder="My Awesome Space"
              autoFocus
              required
            />
          </div>
          <button type="submit" className="w-full btn-primary">
            Create Space
          </button>
        </form>
      </Modal>

      {/* Search/Discover Spaces Modal */}
      <SearchSpacesModal
        isOpen={showSearchSpaces}
        onClose={() => setShowSearchSpaces(false)}
        onJoinSpace={onJoinSpace}
      />

      {/* Space Settings Modal */}
      <SpaceSettingsModal
        isOpen={showSpaceSettings}
        onClose={() => {
          setShowSpaceSettings(false)
          setSelectedSpaceForSettings(null)
        }}
        space={selectedSpaceForSettings}
        userId={profile.id}
        onUpdateSpace={onUpdateSpace}
        onSpaceDeleted={(spaceId) => {
          setShowSpaceSettings(false)
          setSelectedSpaceForSettings(null)
          onDeleteSpace?.(spaceId)
        }}
        toast={toast}
      />
    </>
  )
}
